/**
 * Best-effort front-end policy for the "connect your own domain" input.
 *
 * Rejects domains an owner can't plausibly own: government, military, education
 * and intergovernmental domains, and the registrable domains (plus subdomains) of
 * major brands. The backend stays the authority; this only stops obvious misuse
 * early with an inline message. Lookalikes / typosquats are out of scope.
 */

/** Why a domain is refused (null = allowed). */
export type DomainBlockReason = 'government' | 'brand';

/** Top-level domains reserved for government / military / education / treaty bodies. */
const RESTRICTED_TLDS = new Set(['gov', 'mil', 'edu', 'int']);

/** Second-level labels that mark a public-sector zone under a country TLD
 *  (gov.uk, gob.mx, gouv.fr, gc.ca, govt.nz, go.jp, ac.uk, edu.xx, mil.xx, ...). */
const RESTRICTED_SECOND_LEVELS = new Set(['gov', 'gob', 'gouv', 'govt', 'gv', 'go', 'gc', 'edu', 'ac', 'mil']);

/** Generic second-level labels that form a multi-part public suffix under a
 *  country TLD (co.uk, com.au, net.br, ...), so the brand label sits left of them. */
const GENERIC_SECOND_LEVELS = new Set(['co', 'com', 'net', 'org', 'ne', 'or', 'ltd', 'plc', 'biz', 'info']);

/** Registrable labels of major brands (tech, payments, social, big gambling operators). */
const BRAND_LABELS = new Set([
  'google',
  'youtube',
  'gmail',
  'facebook',
  'instagram',
  'whatsapp',
  'meta',
  'messenger',
  'apple',
  'icloud',
  'microsoft',
  'hotmail',
  'outlook',
  'xbox',
  'amazon',
  'netflix',
  'paypal',
  'x',
  'twitter',
  'tiktok',
  'linkedin',
  'openai',
  'chatgpt',
  'anthropic',
  'visa',
  'mastercard',
  'americanexpress',
  'stripe',
  'spotify',
  'snapchat',
  'telegram',
  'discord',
  'reddit',
  'yahoo',
  'ebay',
  'bet365',
  'betfair',
  'pokerstars',
  'draftkings',
  'fanduel',
  'williamhill',
  'paddypower',
  'betway',
  'bwin',
  'unibet',
  'ladbrokes',
  'skybet',
  'betmgm',
  'caesars',
  '888casino',
  '888poker',
  'stake',
]);

const isCountryTld = (label: string): boolean => /^[a-z]{2}$/.test(label);

/** Split a typed domain into lower-case labels (tolerates a trailing dot). */
const toLabels = (domain: string): string[] =>
  domain
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
    .split('.')
    .filter(Boolean);

/** True for government / military / education / intergovernmental domains. */
export const isRestrictedPublicDomain = (domain: string): boolean => {
  const labels = toLabels(domain);
  if (labels.length === 0) return false;
  const tld = labels[labels.length - 1];
  if (RESTRICTED_TLDS.has(tld)) return true;
  if (!isCountryTld(tld) || labels.length < 2) return false;
  // gov.uk / gob.mx / ac.uk ...
  if (RESTRICTED_SECOND_LEVELS.has(labels[labels.length - 2])) return true;
  // Regional zones one level deeper: gouv.qc.ca, gov.nsw.au ...
  const region = labels[labels.length - 2];
  const third = labels[labels.length - 3];
  return labels.length >= 3 && isCountryTld(region) && RESTRICTED_SECOND_LEVELS.has(third);
};

/**
 * The registrable label: the one directly left of the public suffix, e.g.
 * "google" for google.com, google.co.uk and mail.google.com. Null for a bare suffix.
 */
export const registrableLabel = (domain: string): string | null => {
  const labels = toLabels(domain);
  if (labels.length < 2) return null;
  const tld = labels[labels.length - 1];
  const second = labels[labels.length - 2];
  const suffixLength = isCountryTld(tld) && GENERIC_SECOND_LEVELS.has(second) && labels.length >= 3 ? 2 : 1;
  return labels[labels.length - 1 - suffixLength] ?? null;
};

/** True when the domain (or any subdomain of it) belongs to a major brand. */
export const isBrandDomain = (domain: string): boolean => {
  const label = registrableLabel(domain);
  return label !== null && BRAND_LABELS.has(label);
};

/** Why the domain can't be connected, or null when it passes the policy. */
export const getDomainBlockReason = (domain: string): DomainBlockReason | null => {
  if (isRestrictedPublicDomain(domain)) return 'government';
  if (isBrandDomain(domain)) return 'brand';
  return null;
};
